import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Link2, Plus, Edit, Trash2, ExternalLink, RotateCcw, Unlink } from 'lucide-react';
import { apiRequest } from '../../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function DynamicLinksPage() {
  const navigate = useNavigate();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [formData, setFormData] = useState({ 
    code: '', 
    redirect_url: '',
    link_type: 'review_card' // default to review card
  });

  useEffect(() => {
    checkAuth();
    fetchLinks();
  }, []);

  // Auto-fill redirect URL for digital card type
  useEffect(() => {
    if (formData.link_type === 'digital_card' && formData.code) {
      const profileUrl = `${window.location.origin}/api/profile/${formData.code}`;
      setFormData(prev => ({ ...prev, redirect_url: profileUrl }));
    }
  }, [formData.link_type, formData.code]);

  const checkAuth = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
    }
  };

  const fetchLinks = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await apiRequest(`${BACKEND_URL}/api/admin/dynamic-links`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch dynamic links');
      }

      setLinks(response.data.links || []);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      const method = editingLink ? 'PUT' : 'POST';
      const url = editingLink 
        ? `${BACKEND_URL}/api/admin/dynamic-links/${editingLink.id}`
        : `${BACKEND_URL}/api/admin/dynamic-links`;

      const response = await apiRequest(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(response.data?.detail || 'Failed to save link');
      }

      setShowModal(false);
      setEditingLink(null);
      setFormData({ code: '', redirect_url: '' });
      fetchLinks();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to deactivate this link? It will be moved to the inactive list.')) return;
    try {
      const token = localStorage.getItem('admin_token');
      const response = await apiRequest(`${BACKEND_URL}/api/admin/dynamic-links/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to deactivate link');
      }

      fetchLinks();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRestore = async (id) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await apiRequest(`${BACKEND_URL}/api/admin/dynamic-links/${id}/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to restore link');
      }

      fetchLinks();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUnlinkFromOrder = async (link) => {
    const orderRef = link?.associated_order?.order_id
      ? `#${link.associated_order.order_id.slice(0, 8)}`
      : 'its order';
    if (!window.confirm(`Unlink this dynamic link from ${orderRef}?\n\nThe link will become available to associate with another order.`)) {
      return;
    }
    try {
      const token = localStorage.getItem('admin_token');
      const response = await apiRequest(
        `${BACKEND_URL}/api/admin/dynamic-links/${link.id}/unlink`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(response.data?.detail || 'Failed to unlink');
      }
      fetchLinks();
    } catch (err) {
      setError(err.message);
    }
  };

  const openModal = (link = null) => {
    if (link) {
      setEditingLink(link);
      setFormData({ 
        code: link.code, 
        redirect_url: link.redirect_url,
        link_type: link.link_type || 'review_card'
      });
    } else {
      setEditingLink(null);
      setFormData({ 
        code: '', 
        redirect_url: '',
        link_type: 'review_card'
      });
    }
    setShowModal(true);
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <Link2 className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold text-navy">Dynamic Links</h1>
              <p className="text-gray-600">Manage trackable links for cards</p>
            </div>
          </div>
          <Button
            onClick={() => openModal()}
            className="bg-navy text-white hover:bg-navy/90"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Link
          </Button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading links...</p>
          </div>
        ) : (
          <>
            {/* Active Links */}
            <div className="mb-8">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: '#7D64FA' }}>
                <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                Active Links ({links.filter(l => l.is_active !== false).length})
              </h2>
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Link Code
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Link Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Associated Order
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Internal URL
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Redirect URL
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          QR Scans
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          NFC Taps
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {links.filter(l => l.is_active !== false).map((link) => (
                        <tr key={link.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="font-mono text-sm font-semibold text-navy">{link.code}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              link.link_type === 'digital_card' 
                                ? 'bg-purple-100 text-purple-800' 
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {link.link_type === 'digital_card' ? 'Digital Card' : 'Review Card'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {link.associated_order ? (
                              <div>
                                <p className="font-semibold text-navy">#{link.associated_order.order_id?.slice(0, 8)}</p>
                                <p className="text-xs text-gray-500">{link.associated_order.business_name}</p>
                              </div>
                            ) : (
                              <span className="text-gray-400 italic">Not Associated</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <a 
                              href={`${window.location.origin}/${link.code}`}
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline flex items-center gap-1"
                            >
                              <span className="font-mono truncate max-w-xs">{window.location.origin}/{link.code}</span>
                              <ExternalLink className="w-3 h-3 flex-shrink-0" />
                            </a>
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <a 
                              href={link.redirect_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-gray-600 hover:underline truncate max-w-xs block"
                            >
                              {link.redirect_url}
                            </a>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
                              {link.qr_scans || 0}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-semibold">
                              {link.nfc_taps || 0}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                              Active
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => openModal(link)}
                                className="text-navy hover:text-navy/80"
                                title="Edit"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              {link.associated_order && (
                                <button
                                  onClick={() => handleUnlinkFromOrder(link)}
                                  className="text-orange-600 hover:text-orange-800"
                                  title="Unlink from order"
                                >
                                  <Unlink className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={() => handleDelete(link.id)}
                                className="text-red-600 hover:text-red-800"
                                title="Deactivate"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {links.filter(l => l.is_active !== false).length === 0 && (
                    <div className="text-center py-8 text-gray-500">No active links</div>
                  )}
                </div>
              </div>
            </div>

            {/* Inactive Links */}
            {links.filter(l => l.is_active === false).length > 0 && (
              <div>
                <h2 className="text-lg font-bold text-gray-600 mb-4 flex items-center gap-2">
                  <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                  Inactive Links ({links.filter(l => l.is_active === false).length})
                </h2>
                <div className="bg-white rounded-2xl shadow-sm overflow-hidden opacity-75">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Link Code
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Link Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {links.filter(l => l.is_active === false).map((link) => (
                          <tr key={link.id} className="hover:bg-gray-50 bg-gray-50/50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="font-mono text-sm text-gray-500">{link.code}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                                {link.link_type === 'digital_card' ? 'Digital Card' : 'Review Card'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-semibold">
                                Inactive
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <button
                                onClick={() => handleRestore(link.id)}
                                className="text-green-600 hover:text-green-800 flex items-center gap-1"
                              >
                                <RotateCcw className="w-4 h-4" />
                                <span className="text-xs">Restore</span>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl max-w-md w-full p-8">
              <h2 className="text-2xl font-bold text-navy mb-6">
                {editingLink ? 'Edit' : 'Add'} Dynamic Link
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Link Type *
                  </label>
                  <select
                    value={formData.link_type}
                    onChange={(e) => setFormData({ ...formData, link_type: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                    required
                    disabled={editingLink}
                  >
                    <option value="review_card">Review Card</option>
                    <option value="digital_card">Digital Card</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.link_type === 'digital_card' 
                      ? 'Opens mobile profile view (auto-generated URL)' 
                      : 'Redirects to custom page'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Link Code
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                    placeholder="e.g., ABC123"
                    required
                    disabled={editingLink}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Redirect URL
                  </label>
                  <input
                    type="url"
                    value={formData.redirect_url}
                    onChange={(e) => setFormData({ ...formData, redirect_url: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent"
                    placeholder="https://example.com"
                    required
                    disabled={formData.link_type === 'digital_card'}
                  />
                  {formData.link_type === 'digital_card' && (
                    <p className="text-xs text-gray-500 mt-1">
                      Auto-generated based on link code
                    </p>
                  )}
                </div>
                <div className="flex gap-3 mt-6">
                  <Button
                    type="submit"
                    className="flex-1 bg-navy text-white hover:bg-navy/90"
                  >
                    {editingLink ? 'Update' : 'Create'}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 bg-gray-200 text-gray-700 hover:bg-gray-300"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
